/**
 * Shared enum-like constant tuples for the account REST layer. Extracted from
 * `account.ts` so `account.schema.ts` can build `z.enum(...)`s from them without
 * creating a circular import (`account.ts` imports the schemas, the schemas
 * import these constants). `account.ts` re-exports everything here, so existing
 * `~/v2/api/rest/account` import sites keep working.
 */

export const REVIEW_STATUS = [
  "ANALYST_REVIEW",
  "BULK_APPROVAL",
  "DOCUMENT_UPLOADED",
  "NEW",
  "PENDING_ALPACA",
  "PENDING_USER",
  "PRINCIPAL_DOC_REQUEST",
  "PRINCIPAL_REVIEW_ACCOUNT_UPDATE",
  "PRINCIPAL_REVIEW_ENTITY",
  "PRINCIPAL_REVIEW_ESCALATION",
  "PRINCIPAL_REVIEW_USER_REQUEST",
  "WATCHLIST_HIT_ESCALATION",
  "WATCHLIST_HIT_REVIEW",
  "WATCHLIST_HIT_UNRESOLVED",
] as const;

export const DOCUMENT_TYPES = [
  "date_of_birth_verification",
  "social_security_number_verification",
  "address_verification",
  "address_vertification", // due to a typo
  "account_approval_letter",
  "identity_verification",
  "w_8ben",
  "tax_id_verification",
  "selfie",
  "entity_registration",
  "w_8bene",
  "company_formation",
  "other",
] as const;

export const DOCUMENT_SUB_TYPES = [
  "407_letter",
  "acat_form",
  "account_application",
  "bank_statement",
  "cip_docv",
  "cip",
  "drivers_license",
  "Form W-8BEN",
  "government_id",
  "lease_agreement",
  "letter_of_authorization",
  "passport",
  "permanent_resident_card",
  "poa",
  "ssa_certified_letter",
  "ssn_card",
  "statement",
  "utility_bill",
  "return_mail",
  "return_email",
  "will",
  "death_certificate",
  "trust_certification",
  "beneficiary_form",
  "court_order",
  "divorce_decree",
  "letter_of_testamentary",
  "b-notice_letter_1",
  "b-notice_letter_2",
  "w-8ben-e",
  "w-8imy",
  "",
] as const;

export const ACCOUNT_TYPES = [
  "trading",
  "custodial",
  "donor_advised",
  "ira",
  "trust",
  "omnibus_non_disclosed",
  "omnibus_sub",
  "legal_entity",
  "dvp",
  "hsa",
] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const ACCOUNT_SUB_TYPES = ["traditional", "roth"] as const;
export type AccountSubType = (typeof ACCOUNT_SUB_TYPES)[number];

export const BENEFICIARY_RELATIONSHIPS = ["spouse", "other"] as const;
export type BeneficiaryRelationship =
  (typeof BENEFICIARY_RELATIONSHIPS)[number];

export const BENEFICIARY_TYPES = ["primary", "contingent"] as const;
export type BeneficiaryType = (typeof BENEFICIARY_TYPES)[number];

export const INVESTMENT_TIME_HORIZON_VALUES = [
  "less_than_1_year",
  "1_to_2_years",
  "3_to_5_years",
  "6_to_10_years",
  "more_than_10_years",
] as const;
export type InvestmentTimeHorizon =
  (typeof INVESTMENT_TIME_HORIZON_VALUES)[number];

export const INVESTMENT_OBJECTIVE_VALUES = [
  "generate_income",
  "preserve_wealth",
  "market_speculation",
  "growth",
  "balance_preserve_wealth_with_growth",
] as const;
export type InvestmentObjective = (typeof INVESTMENT_OBJECTIVE_VALUES)[number];

export const LIQUIDITY_NEEDS_VALUES = [
  "very_important",
  "important",
  "somewhat_important",
  "does_not_matter",
] as const;
export type LiquidityNeeds = (typeof LIQUIDITY_NEEDS_VALUES)[number];

export const RISK_TOLERANCE_VALUES = [
  "conservative",
  "moderate",
  "significant_risk",
] as const;
export type RiskTolerance = (typeof RISK_TOLERANCE_VALUES)[number];

export const OPTIONS_LEVELS = [1, 2, 3] as const;
export type OptionsLevel = (typeof OPTIONS_LEVELS)[number];
